'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { DownloadIcon, PackageIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface CompressToolProps {
  images: File[];
}

interface CompressedImage {
  original: File;
  compressed?: Blob;
  originalSize: number;
  compressedSize?: number;
  originalUrl?: string;
  compressedUrl?: string;
}

export function CompressTool({ images }: CompressToolProps) {
  const [quality, setQuality] = useState([80]);
  const [outputFormat, setOutputFormat] = useState<'png' | 'jpeg' | 'webp'>('jpeg');
  const [maxWidth, setMaxWidth] = useState([2000]);
  const [maxHeight, setMaxHeight] = useState([2000]);
  const [compressedImages, setCompressedImages] = useState<CompressedImage[]>([]);
  const [isCompressing, setIsCompressing] = useState(false);

  // 初始化压缩图片列表
  useEffect(() => {
    if (images.length > 0) {
      const newCompressedImages: CompressedImage[] = images.map((file) => ({
        original: file,
        originalSize: file.size,
        originalUrl: URL.createObjectURL(file),
      }));
      setCompressedImages(newCompressedImages);
    }
    
    // 清理
    return () => {
      compressedImages.forEach(img => {
        if (img.originalUrl) URL.revokeObjectURL(img.originalUrl);
        if (img.compressedUrl) URL.revokeObjectURL(img.compressedUrl);
      });
    };
  }, [images]);

  const handleCompress = useCallback(async () => {
    setIsCompressing(true);
    
    // 清理之前的压缩结果
    compressedImages.forEach(img => {
      if (img.compressedUrl) URL.revokeObjectURL(img.compressedUrl);
    });

    const results: CompressedImage[] = [];
    
    for (const img of compressedImages) {
      const originalFile = img.original;
      const imgElement = document.createElement('img');
      
      await new Promise<void>((resolve) => {
        imgElement.onload = () => resolve();
        imgElement.onerror = () => resolve();
        imgElement.src = img.originalUrl || '';
      });
      
      if (!imgElement.src) {
        results.push(img);
        continue;
      }
      
      // 计算缩放后的尺寸
      let width = imgElement.naturalWidth;
      let height = imgElement.naturalHeight;
      
      if (width > maxWidth[0] || height > maxHeight[0]) {
        const ratio = Math.min(maxWidth[0] / width, maxHeight[0] / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        results.push(img);
        continue;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // 对于 JPEG 使用白色背景
      if (outputFormat === 'jpeg') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      
      ctx.drawImage(imgElement, 0, 0, width, height);
      
      // 压缩
      const format = outputFormat === 'jpeg' ? 'image/jpeg' : outputFormat === 'webp' ? 'image/webp' : 'image/png';
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, format, quality[0] / 100);
      });
      
      if (blob) {
        results.push({
          ...img,
          compressed: blob,
          compressedSize: blob.size,
          compressedUrl: URL.createObjectURL(blob),
        });
      } else {
        results.push(img);
      }
    }
    
    setCompressedImages(results);
    setIsCompressing(false);
  }, [compressedImages, quality, outputFormat, maxWidth, maxHeight]);

  const handleDownloadSingle = (compressed: CompressedImage) => {
    if (!compressed.compressed) return;
    
    const url = URL.createObjectURL(compressed.compressed);
    const link = document.createElement('a');
    link.href = url;
    link.download = `compressed-${compressed.original.name}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadAllAsZip = async () => {
    if (!compressedImages.some(img => img.compressed)) return;
    
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    
    compressedImages.forEach((img, index) => {
      if (!img.compressed) return;
      
      // 生成唯一的文件名
      const ext = img.original.name.split('.').pop() || outputFormat;
      const baseName = img.original.name.replace(/\.[^.]+$/, '');
      const filename = `${baseName}_compressed.${outputFormat}`;
      
      zip.file(filename, img.compressed);
    });
    
    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const link = document.createElement('a');
    link.href = url;
    link.download = `compressed-images-${Date.now()}.zip`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getCompressionRatio = (original: number, compressed: number): string => {
    if (original === 0) return '0%';
    const ratio = ((original - compressed) / original) * 100;
    return ratio > 0 ? `${ratio.toFixed(1)}%` : '0%';
  };

  const totalOriginalSize = compressedImages.reduce((sum, img) => sum + img.originalSize, 0);
  const totalCompressedSize = compressedImages.reduce((sum, img) => sum + (img.compressedSize || img.originalSize), 0);
  const hasCompressedImages = compressedImages.some(img => img.compressed);

  return (
    <div className="space-y-6">
      {/* 工具栏 */}
      <div className="space-y-4 rounded-lg border bg-muted/50 p-4">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm">输出格式</Label>
              <Select value={outputFormat} onValueChange={(val) => setOutputFormat(val as 'png' | 'jpeg' | 'webp')}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="jpeg">JPEG</SelectItem>
                  <SelectItem value="png">PNG</SelectItem>
                  <SelectItem value="webp">WebP</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-sm">质量</Label>
              <Slider
                value={quality}
                onValueChange={setQuality}
                max={100}
                min={10}
                step={5}
                className="w-40"
              />
              <span className="w-12 text-sm">{quality[0]}%</span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm">最大宽度</Label>
              <Slider
                value={maxWidth}
                onValueChange={(val) => {
                  setMaxWidth(val);
                  setMaxHeight(val);
                }}
                max={4000}
                min={200}
                step={100}
                className="w-32"
              />
              <span className="w-16 text-sm">{maxWidth[0]}px</span>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-sm">最大高度</Label>
              <Slider
                value={maxHeight}
                onValueChange={setMaxHeight}
                max={4000}
                min={200}
                step={100}
                className="w-32"
              />
              <span className="w-16 text-sm">{maxHeight[0]}px</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={handleCompress}
            disabled={isCompressing}
          >
            {isCompressing ? '压缩中...' : '开始压缩'}
          </Button>
          
          {hasCompressedImages && (
            <Button variant="outline" onClick={handleDownloadAllAsZip}>
              <PackageIcon className="mr-2 h-4 w-4" />
              下载 ZIP 压缩包
            </Button>
          )}
        </div>
      </div>

      {/* 统计信息 */}
      {compressedImages.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-lg border bg-card p-4 text-center">
            <div className="text-2xl font-bold">{compressedImages.length}</div>
            <div className="text-sm text-muted-foreground">图片数量</div>
          </div>
          <div className="rounded-lg border bg-card p-4 text-center">
            <div className="text-2xl font-bold">{formatFileSize(totalOriginalSize)}</div>
            <div className="text-sm text-muted-foreground">原始大小</div>
          </div>
          <div className="rounded-lg border bg-card p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {compressedImages[0].compressed ? formatFileSize(totalCompressedSize) : '-'}
            </div>
            <div className="text-sm text-muted-foreground">
              压缩后 {totalOriginalSize > 0 && compressedImages[0].compressed 
                ? `- ${getCompressionRatio(totalOriginalSize, totalCompressedSize)}` 
                : ''}
            </div>
          </div>
        </div>
      )}

      {/* 压缩结果列表 */}
      <div className="space-y-4">
        <h3 className="font-medium">压缩结果</h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {compressedImages.map((img, index) => (
            <div key={index} className="rounded-lg border bg-card overflow-hidden">
              {/* 预览图 */}
              <div className="relative aspect-video bg-muted">
                {img.compressedUrl ? (
                  <img
                    src={img.compressedUrl}
                    alt=""
                    className="h-full w-full object-contain"
                  />
                ) : img.originalUrl ? (
                  <img
                    src={img.originalUrl}
                    alt=""
                    className="h-full w-full object-contain"
                  />
                ) : null}
              </div>
              
              {/* 信息 */}
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate font-medium">{img.original.name}</span>
                  {img.compressed && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      getCompressionRatio(img.originalSize, img.compressedSize || 0).startsWith('-')
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {getCompressionRatio(img.originalSize, img.compressedSize || 0)}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>原始: {formatFileSize(img.originalSize)}</span>
                  <span>
                    {img.compressedSize 
                      ? `压缩后: ${formatFileSize(img.compressedSize)}` 
                      : '未压缩'}
                  </span>
                </div>

                {img.compressed && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => handleDownloadSingle(img)}
                  >
                    <DownloadIcon className="mr-2 h-4 w-4" />
                    下载
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 说明 */}
      <div className="rounded-lg border bg-muted/50 p-4">
        <h3 className="mb-2 font-medium">使用说明</h3>
        <ul className="space-y-1 text-sm text-muted-foreground">
          <li>• 调整质量滑块控制压缩程度，值越低文件越小</li>
          <li>• 设置最大宽高可以限制输出图片尺寸</li>
          <li>• JPEG 格式压缩率最高，WebP 格式在保持质量的同时体积更小</li>
          <li>• PNG 格式会保留透明背景但不支持质量压缩</li>
          <li>• 点击「下载 ZIP 压缩包」可一键下载所有压缩后的图片</li>
        </ul>
      </div>
    </div>
  );
}
